import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

/**
 * «Розумне повторення»: questions of closed tests come back two weeks later, three a day; a right
 * answer moves a question twice as far, a wrong one brings it back in three days. «Дерево знань»:
 * the topics of each subject — done, to repeat, now, ahead.
 */
export interface ReviewQuestion {
  id: string;
  subject: string;
  topic: string | null;
  text: string;
  options: string[];
  multiple: boolean;
}

export interface ReviewResult {
  correct: boolean;
  right: number[];
  nextOn: string;
}

export function useReviewToday(enabled = true) {
  const db = useDb();
  return useQuery({
    queryKey: ['review-today'],
    enabled,
    // the set of the day stays while the student answers it
    staleTime: Infinity,
    queryFn: async (): Promise<ReviewQuestion[]> =>
      must(await db.rpc('review_today')).map((r) => ({
        id: r.question_id,
        subject: r.subject,
        topic: r.topic,
        text: r.question,
        options: r.options,
        multiple: r.multiple,
      })),
  });
}

export function useReviewAnswer() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: { questionId: string; chosen: number[] }): Promise<ReviewResult> => {
      const row = must(
        await db.rpc('review_answer', { p_question: a.questionId, p_chosen: a.chosen }),
      )[0];
      if (!row) throw new Error('no answer');
      return { correct: row.correct, right: row.right_options, nextOn: row.next_on };
    },
    onSettled: () => void client.invalidateQueries({ queryKey: ['review-cards'] }),
  });
}

export interface ReviewCardsSummary {
  total: number;
  /** answered right at least once in a row now */
  remembered: number;
  /** the next day something is due, if nothing is due today */
  nextOn: string | null;
}

/** How the repetition goes: for the student and the family. */
export function useReviewSummary(studentId: string, today: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['review-cards', studentId],
    enabled: studentId !== '',
    queryFn: async (): Promise<ReviewCardsSummary> => {
      const rows = must(
        await db.from('review_cards').select('due_on, streak').eq('student_member_id', studentId),
      );
      const later = rows
        .map((r) => r.due_on)
        .filter((d) => d > today)
        .sort();
      return {
        total: rows.length,
        remembered: rows.filter((r) => r.streak > 0).length,
        nextOn: later[0] ?? null,
      };
    },
  });
}

export type TopicStatus = 'done' | 'weak' | 'now' | 'next';

export interface TreeTopic {
  courseId: string;
  subject: string;
  topic: string;
  ord: number;
  lessonsHeld: number;
  lessonsTotal: number;
  avgPoints: number | null;
  grades: number;
  quizPct: number | null;
  status: TopicStatus;
}

export interface TreeSubject {
  subject: string;
  courseId: string;
  topics: TreeTopic[];
  done: number;
  weak: number;
}

/** Topics by subject, in the order of the lessons; subjects alphabetically. */
export function treeBySubject(topics: readonly TreeTopic[]): TreeSubject[] {
  const map = new Map<string, TreeSubject>();
  for (const t of topics) {
    const key = t.courseId;
    const entry = map.get(key) ?? {
      subject: t.subject,
      courseId: key,
      topics: [],
      done: 0,
      weak: 0,
    };
    entry.topics.push(t);
    if (t.status === 'done') entry.done += 1;
    if (t.status === 'weak') entry.weak += 1;
    map.set(key, entry);
  }
  return [...map.values()]
    .map((s) => ({ ...s, topics: [...s.topics].sort((a, b) => a.ord - b.ord) }))
    .sort((a, b) => a.subject.localeCompare(b.subject, 'uk'));
}

export function useKnowledgeTree(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['knowledge-tree', studentId],
    enabled: studentId !== '',
    queryFn: async (): Promise<TreeSubject[]> =>
      treeBySubject(
        must(await db.rpc('knowledge_tree', { p_student: studentId })).map((r) => ({
          courseId: r.course_id,
          subject: r.subject,
          topic: r.topic,
          ord: r.ord,
          lessonsHeld: r.lessons_held,
          lessonsTotal: r.lessons_total,
          avgPoints: r.avg_points === null ? null : Number(r.avg_points),
          grades: r.grades,
          quizPct: r.quiz_pct,
          status: r.status as TopicStatus,
        })),
      ),
  });
}
